import { Injectable, inject, signal } from '@angular/core'
import { signInWithCustomToken } from 'firebase/auth'
import { httpsCallable } from 'firebase/functions'
import { environment } from 'src/environments/environment'
import { FirebaseService } from './firebase.service'

export type TeamsSsoStatus =
  | 'idle'
  | 'initializing'
  | 'authenticating'
  | 'success'
  | 'error'
  | 'user-not-found'

/**
 * Microsoft Teams compatibility (port of svelte `src/lib/utils/teams.ts`).
 *
 * When the app is embedded in a Teams tab we sign the user in via Entra SSO:
 * silent `getAuthToken()` -> exchange for a Firebase custom token at the
 * `exchangeTeamsToken` Cloud Function -> `signInWithCustomToken`. This bypasses
 * the centralized auth portal, whose interactive redirect can't complete inside
 * the Teams iframe. `angularFireAuthGuard` (`app-auth-guard.ts`) consumes this
 * service — that's the guard that actually runs in this app.
 */
@Injectable({ providedIn: 'root' })
export class TeamsService {
  private readonly firebase = inject(FirebaseService)

  readonly isTeamsEnvironment = signal(false)
  readonly ssoStatus = signal<TeamsSsoStatus>('idle')
  readonly ssoError = signal<string | null>(null)

  // Run the token exchange once per app load (see auth.guard.ts re-pin note),
  // even when a Firebase session already exists.
  private authPending = true

  // Cache detection so every caller (bootstrap init + auth guard) shares a
  // single Teams SDK init and a single result — no races, no double work.
  private detectionPromise: Promise<boolean> | null = null

  /** True exactly once per app load, then false. */
  consumeAuthPending(): boolean {
    if (this.authPending) {
      this.authPending = false
      return true
    }
    return false
  }

  /**
   * Fast synchronous pre-check. Teams always hosts the app in an iframe (web
   * tab) or a webview exposing `nativeInterface` (desktop/mobile client). When
   * we're the top-level window with no native bridge we cannot be inside Teams,
   * so we skip the SDK round-trip entirely and don't delay normal sign-in.
   */
  private couldBeInTeams(): boolean {
    if (typeof window === 'undefined') return false
    try {
      if (typeof (window as unknown as { nativeInterface?: unknown }).nativeInterface !== 'undefined') return true
      return window.parent !== window.self
    } catch {
      // Cross-origin access to window.parent throws — that means we're framed
      // by another origin, which is exactly the Teams web-tab case.
      return true
    }
  }

  /**
   * Detect (once) whether we're running inside Microsoft Teams and initialize
   * the Teams SDK if so. Result is cached and shared across all callers.
   */
  initializeTeams(): Promise<boolean> {

    if (this.detectionPromise) return this.detectionPromise

    this.detectionPromise = (async () => {
      if (!this.couldBeInTeams()) {
        this.isTeamsEnvironment.set(false)
        this.ssoStatus.set('idle')
        return false
      }

      this.ssoStatus.set('initializing')
      try {
        const { app } = await import('@microsoft/teams-js')
        // app.initialize() never resolves outside a real Teams host (it waits
        // for a handshake that won't arrive), so bound it with a timeout to keep
        // a non-Teams iframe from hanging the auth flow indefinitely.
        await Promise.race([
          app.initialize(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Teams init timed out')), 3000)),
        ])

        this.isTeamsEnvironment.set(true)
        this.ssoStatus.set('idle')
        return true
      } catch {
        this.isTeamsEnvironment.set(false)
        this.ssoStatus.set('idle')
        return false
      }
    })()

    return this.detectionPromise
  }

  /** Await the shared Teams detection, starting it if it hasn't begun yet. */
  ensureTeamsDetected(): Promise<boolean> {
    return this.initializeTeams()
  }

  /**
   * Open an external URL from inside Teams via the Teams SDK so it lands in the
   * system browser (interactive flows like org setup can't run in the iframe).
   * Falls back to window.open when the SDK call isn't available.
   */
  async openTeamsLink(url: string): Promise<void> {
    try {
      const { app } = await import('@microsoft/teams-js')
      await app.openLink(url)
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  /**
   * Full Teams SSO flow: silent AAD token -> exchange for a Firebase custom
   * token via `exchangeTeamsToken` -> sign in. Drives the status/error signals.
   */
  async performTeamsSso(): Promise<void> {
    this.ssoStatus.set('authenticating')
    this.ssoError.set(null)

    try {
      const { authentication } = await import('@microsoft/teams-js')
      const aadToken = await authentication.getAuthToken()

      const exchange = httpsCallable<{ aadToken: string }, { customToken: string }>(
        // Mints a GLOBAL session token → the global project's function.
        this.firebase.globalFunctions,
        'exchangeTeamsToken',
      )
      const result = await exchange({ aadToken })
      const { customToken } = result.data

      await signInWithCustomToken(this.firebase.auth!, customToken)
      this.ssoStatus.set('success')
    } catch (error: unknown) {
      const err = error as { code?: string; message?: string; details?: { code?: string } }
      const code = err?.code || err?.details?.code || ''
      const message = err?.message || 'Teams SSO failed'

      // Backend throws HttpsError('not-found', 'USER_NOT_FOUND'); the client
      // FirebaseError message is 'USER_NOT_FOUND' (code may be 'functions/not-found'),
      // so match on either — same dual check as svelte.
      if (code === 'not-found' || message.includes('USER_NOT_FOUND')) {
        this.ssoStatus.set('user-not-found')
        this.ssoError.set('User not found in the system')
      } else {
        this.ssoStatus.set('error')
        this.ssoError.set(message)
      }

      throw error
    }
  }
}
